<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  import { AssetsService } from '@client/services/assets';

  import { updateTimelineData } from '@client/utils/video-report';

  import { secondsToHms } from '@common/utils/numbers';

  import Chart from './metrics/metrics';

  export let data = {};
  export let loading = false;
  export let history = [];

  let analyticsData = {};

  let metrics = {
    duration: [],
    viewed: [],
    session: []
  };

  const colors = {
    duration: '#43A0FC',
    viewed: '#D9BC61',
    low: '#92E2C0',
    high: '#ec6454'
  };

  const generateMetricDataset = (series, label, color) => {
    if (series?.length) {
      return {
        datasets: [
          {
            type: 'line',
            label,
            fill: true,
            borderColor: color,
            backgroundColor: color + 10,
            tension: 0.3,
            data: series.map(([x, y]) => ({ x, y }))
          }
        ]
      };
    }

    return null;
  };

  const generateSessionsMetricDataset = (lowData, highData) => {
    if (lowData?.length && highData?.length) {
      return {
        datasets: [
          {
            type: 'line',
            label: $format('label.SESSION_START'),
            fill: true,
            borderColor: colors.low,
            backgroundColor: colors.low + 10,
            tension: 0.3,
            data: lowData.map(([x, y]) => ({ x, y }))
          },
          {
            type: 'line',
            label: $format('label.SESSION_END'),
            fill: true,
            borderColor: colors.high,
            backgroundColor: colors.high + 10,
            tension: 0.3,
            data: highData.map(([x, y]) => ({ x, y }))
          }
        ]
      };
    }

    return null;
  };

  const formatValue = (value) => {
    if (value.includes(',')) {
      value = value.replace(/,/g, '');
    }

    return secondsToHms(value);
  };

  const fetchVideoMetrics = async () => {
    loading = true;

    const response = await AssetsService.getVideoMetrics(data._id, data.vidid);

    analyticsData = updateTimelineData(response);
    history = response;

    metrics.duration = generateMetricDataset(
      analyticsData.durationSeries,
      $format('label.TOTAL_DURATION'),
      colors.duration
    );
    metrics.viewed = generateMetricDataset(analyticsData.viewedSeries, $format('label.VIEWS'), colors.viewed);
    metrics.session = generateSessionsMetricDataset(analyticsData.lowSeries, analyticsData.highSeries);

    loading = false;
  };

  onMount(async () => await fetchVideoMetrics());
</script>

<div class="video-report">
  {#if analyticsData?.durationTotal !== 0 && analyticsData?.avgDuration !== undefined && analyticsData?.viewedTotal !== 0}
    <div class="report">
      <div class="charts-layout">
        <div class="chart">
          <div class="chart-metric">
            <div class="chart-metric-total">{(secondsToHms(analyticsData?.avgDuration) || 0).toLocaleString()}</div>
            <div class="chart-metric-label">
              <span>{$format('label.AVERAGE_DURATION')}</span>
              <Icon name="help-circle" id="tooltip-video-duration" />
              <Tooltip target="tooltip-video-duration" placement="top">
                {$format('label.TOOLTIP.AVERAGE_DURATION')}
              </Tooltip>
            </div>
          </div>
          <div class="chart-wrap">
            <Chart bind:data={metrics.duration} {formatValue} />
          </div>
        </div>
        <div class="chart">
          <div class="chart-metric">
            <div class="chart-metric-total">
              {(analyticsData?.viewedTotal || 0).toLocaleString()}
            </div>
            <div class="chart-metric-label">
              <span>{$format('label.VIEWS')}</span>
              <Icon name="help-circle" id="tooltip-video-views" />
              <Tooltip target="tooltip-video-views" placement="top">
                {$format('label.TOOLTIP.SUMMATION_CHART', {
                  values: { type: $format('label.VIDEO'), action: $format('label.VIEWED').toLowerCase() }
                })}
              </Tooltip>
            </div>
          </div>
          <div class="chart-wrap">
            <Chart bind:data={metrics.viewed} />
          </div>
        </div>
        <div class="chart">
          <div class="chart-metric">
            <div class="chart-metric-total">
              {(analyticsData?.viewedTotal || 0).toLocaleString()}
            </div>
            <div class="chart-metric-label">
              <span>{$format('label.SESSIONS')}</span>
              <Icon name="help-circle" id="tooltip-video-sessions" />
              <Tooltip target="tooltip-video-sessions" placement="top">
                {$format('label.TOOLTIP.VIDEO_SESSIONS')}
              </Tooltip>
            </div>
          </div>
          <div class="chart-wrap">
            <Chart bind:data={metrics.session} {formatValue} />
          </div>
        </div>
      </div>
    </div>
  {:else}
    <div class="empty-state">
      <p>{$format('label.NO_VIDEO_REPORT_AVAILABLE')}</p>
    </div>
  {/if}
</div>

<style lang="scss" src="./video-report.scss"></style>
